import { apiClient } from '../lib/apiClient'
import { legalService } from './legalService'

export type AuditAction =
  | 'project.created' | 'project.updated' | 'project.deleted'
  | 'resource.created' | 'resource.updated' | 'resource.deleted'
  | 'allocation.created' | 'allocation.updated' | 'allocation.deleted'
  | 'customer.created' | 'customer.updated' | 'customer.deleted'
  | 'timesheet.created' | 'timesheet.updated' | 'timesheet.deleted'
  | 'member.invited' | 'member.added' | 'member.role_changed' | 'member.removed' | 'member.deactivated' | 'member.departments_updated'
  | 'org.settings_updated' | 'org.subscription_changed'
  | 'auth.login.success' | 'auth.login.failed' | 'auth.logout'
  | 'auth.signup' | 'auth.password_reset' | 'auth.mfa_enabled' | 'auth.mfa_disabled'
  | 'auth.session_expired'
  | 'authorization.failed' | 'authorization.denied'
  | 'data.exported' | 'data.imported'
  | 'security.suspicious_activity' | 'security.rate_limit_exceeded'
  | 'security.account_locked' | 'security.account_unlocked'

interface LogParams {
  orgId: string
  actorUserId: string
  actorDisplayName: string
  action: AuditAction
  entityType: string
  entityId?: string
  entityLabel?: string
  oldValues?: Record<string, unknown>
  newValues?: Record<string, unknown>
  metadata?: Record<string, unknown>
  ipAddress?: string
  userAgent?: string
}

async function log(params: LogParams) {
  try {
    let ipAddress = params.ipAddress || 'unknown';

    if (!params.ipAddress) {
      try {
        const clientInfo = await legalService.captureClientInfo();
        ipAddress = clientInfo.ip_address;
      } catch (err) {
        console.warn('Failed to capture client info for audit log:', err);
      }
    }

    await apiClient.post(`/orgs/${params.orgId}/audit-logs`, {
      actorDisplayName: params.actorDisplayName,
      action: params.action,
      entityType: params.entityType,
      entityId: params.entityId,
      entityLabel: params.entityLabel,
      oldValues: params.oldValues || null,
      newValues: params.newValues || null,
      ipAddress,
    });
  } catch (err) {
    console.error('Failed to write audit log:', err);
  }
}

async function logAuthEvent(
  action: AuditAction,
  userId: string,
  email: string,
  success: boolean,
  metadata?: Record<string, unknown>
) {
  // Auth events occur before org context is known; log to console only
  console.info('auth event', { action, userId, email, success, metadata });
}

async function logSecurityEvent(
  action: AuditAction,
  userId: string | null,
  details: string,
  metadata?: Record<string, unknown>
) {
  // Security events without org context cannot be written to org-scoped endpoint
  console.info('security event', { action, userId, details, metadata });
}

async function getLogs(orgId: string, limit = 200) {
  return apiClient.get<any[]>(`/orgs/${orgId}/audit-logs?limit=${limit}`)
}

async function getSecurityLogs(orgId: string, limit = 100) {
  return apiClient.get<any[]>(`/orgs/${orgId}/audit-logs/security?limit=${limit}`)
}

export const auditService = {
  log,
  getLogs,
  logAuthEvent,
  logSecurityEvent,
  getSecurityLogs,
}
